import { useCallback, useMemo, useRef, useState } from 'react'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'

export interface PlaceDetails {
  readonly googlePlaceId: string
  readonly placeName: string
  readonly countryName?: string
  readonly stopId?: number
}

export interface InlinePlace {
  readonly googlePlaceId: string
  readonly placeName: string
}

export interface OfferDrawerData {
  readonly offerId: string
  readonly accommodationPropertyId?: string
  readonly searchParams?: OfferClickSearchParams
  readonly offerType?: string
  readonly scrollToPricing?: boolean
  readonly scrollToRoomOptions?: boolean
}

export interface UseDrawerStackStateReturn {
  topEntry: DrawerStackEntry | undefined
  previousEntry: DrawerStackEntry | undefined
  isAnyDrawerOpen: boolean
  hasPreviousDrawer: boolean
  shouldAnimateTopEntry: boolean
  openDetailPanel: (data: PlaceDetails) => void
  openPlaceInfo: (data: InlinePlace) => void
  openLocationInfo: (data: LocationInfoData) => void
  openOfferDrawer: (data: OfferDrawerData) => void
  goBack: () => void
  closeAllDrawers: () => void
  afterClose: () => void
  resetDrawers: () => void
}

export type DrawerStackEntry =
  | { id: string; kind: 'detailPanel'; data: PlaceDetails }
  | { id: string; kind: 'placeInfo'; data: InlinePlace }
  | { id: string; kind: 'locationInfo'; data: LocationInfoData }
  | { id: string; kind: 'offerDrawer'; data: OfferDrawerData }

type DrawerStackEntryInput =
  | { kind: 'detailPanel'; data: PlaceDetails }
  | { kind: 'placeInfo'; data: InlinePlace }
  | { kind: 'locationInfo'; data: LocationInfoData }
  | { kind: 'offerDrawer'; data: OfferDrawerData }

interface DrawerStackState {
  stack: Array<DrawerStackEntry>
  isOpen: boolean
  shouldAnimateTopEntry: boolean
}

const MAX_DRAWER_STACK_DEPTH = 20

const INITIAL_DRAWER_STACK_STATE: DrawerStackState = {
  stack: [],
  isOpen: false,
  shouldAnimateTopEntry: false,
}

function isSameDrawerTarget(
  currentEntry: DrawerStackEntry | undefined,
  nextEntry: DrawerStackEntryInput,
) {
  if (!currentEntry) return false

  switch (currentEntry.kind) {
    case 'detailPanel':
      if (nextEntry.kind !== 'detailPanel') return false
      return currentEntry.data.googlePlaceId === nextEntry.data.googlePlaceId &&
        currentEntry.data.stopId === nextEntry.data.stopId
    case 'placeInfo':
      if (nextEntry.kind !== 'placeInfo') return false
      return currentEntry.data.googlePlaceId === nextEntry.data.googlePlaceId
    case 'locationInfo':
      if (nextEntry.kind !== 'locationInfo') return false
      return currentEntry.data.title === nextEntry.data.title &&
        currentEntry.data.placeSearch === nextEntry.data.placeSearch
    case 'offerDrawer':
      if (nextEntry.kind !== 'offerDrawer') return false
      return currentEntry.data.offerId === nextEntry.data.offerId &&
        currentEntry.data.offerType === nextEntry.data.offerType &&
        currentEntry.data.accommodationPropertyId === nextEntry.data.accommodationPropertyId
  }
}

export function useDrawerStackState(): UseDrawerStackStateReturn {
  const [drawerStackState, setDrawerStackState] = useState<DrawerStackState>(
    INITIAL_DRAWER_STACK_STATE,
  )
  const nextDrawerEntryIdRef = useRef(0)

  const pushDrawer = useCallback((entry: DrawerStackEntryInput) => {
    setDrawerStackState(current => {
      const currentStack = current.isOpen ? current.stack : []
      const currentTopEntry = currentStack.at(-1)
      if (isSameDrawerTarget(currentTopEntry, entry)) {
        const nextEntry = { ...currentTopEntry, data: entry.data } as DrawerStackEntry
        return {
          stack: [...currentStack.slice(0, -1), nextEntry],
          isOpen: true,
          shouldAnimateTopEntry: false,
        }
      }

      nextDrawerEntryIdRef.current += 1
      const nextEntry = {
        ...entry,
        id: `drawer-${nextDrawerEntryIdRef.current}`,
      } as DrawerStackEntry

      return {
        stack: [...currentStack, nextEntry].slice(-MAX_DRAWER_STACK_DEPTH),
        isOpen: true,
        shouldAnimateTopEntry: currentStack.length === 0,
      }
    })
  }, [])

  const openDetailPanel = useCallback((data: PlaceDetails) => {
    pushDrawer({ kind: 'detailPanel', data })
  }, [pushDrawer])

  const openPlaceInfo = useCallback((data: InlinePlace) => {
    pushDrawer({ kind: 'placeInfo', data })
  }, [pushDrawer])

  const openLocationInfo = useCallback((data: LocationInfoData) => {
    pushDrawer({ kind: 'locationInfo', data })
  }, [pushDrawer])

  const openOfferDrawer = useCallback((data: OfferDrawerData) => {
    pushDrawer({ kind: 'offerDrawer', data })
  }, [pushDrawer])

  const goBack = useCallback(() => {
    setDrawerStackState(current => {
      if (!current.isOpen || current.stack.length <= 1) return current

      return {
        stack: current.stack.slice(0, -1),
        isOpen: true,
        shouldAnimateTopEntry: false,
      }
    })
  }, [])

  const closeAllDrawers = useCallback(() => {
    setDrawerStackState(current => {
      if (current.stack.length === 0 || !current.isOpen) return current

      return {
        ...current,
        isOpen: false,
      }
    })
  }, [])

  const resetDrawers = useCallback(() => {
    setDrawerStackState(INITIAL_DRAWER_STACK_STATE)
  }, [])

  const afterClose = useCallback(() => {
    setDrawerStackState(current => {
      if (current.isOpen) return current

      return INITIAL_DRAWER_STACK_STATE
    })
  }, [])

  const topEntry = drawerStackState.stack.at(-1)
  const previousEntry = drawerStackState.stack.at(-2)
  const isAnyDrawerOpen = drawerStackState.stack.length > 0 && drawerStackState.isOpen
  const hasPreviousDrawer = drawerStackState.stack.length > 1
  const shouldAnimateTopEntry = drawerStackState.shouldAnimateTopEntry

  return useMemo(() => ({
    topEntry,
    previousEntry,
    isAnyDrawerOpen,
    hasPreviousDrawer,
    shouldAnimateTopEntry,
    openDetailPanel,
    openPlaceInfo,
    openLocationInfo,
    openOfferDrawer,
    goBack,
    closeAllDrawers,
    afterClose,
    resetDrawers,
  }), [
    afterClose,
    closeAllDrawers,
    goBack,
    hasPreviousDrawer,
    isAnyDrawerOpen,
    openDetailPanel,
    openLocationInfo,
    openOfferDrawer,
    openPlaceInfo,
    previousEntry,
    resetDrawers,
    shouldAnimateTopEntry,
    topEntry,
  ])
}
